<script lang="ts" setup>
import { useBigUnderlineEffect, useResponsive } from "@composables"
import { useTemplateRef } from "vue"

const { isTablet, isDesktop, isMobile } = useResponsive()

const section = useTemplateRef("section")
useBigUnderlineEffect(section)

const formIllustrations = [
    {
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/q_auto,f_auto/v1745853293/form_step1_qmvjzb.png",
        alt: "Première étape du formulaire",
    },
    {
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/q_auto,f_auto/v1745853293/form_step2_ov8l3w.png",
        alt: "Deuxième étape du formulaire",
    },
    {
        logo: "https://res.cloudinary.com/dcoutwkb1/image/upload/q_auto,f_auto/v1745853293/form_step3_tzb9pj.png",
        alt: "Troisième étape du formulaire",
    },
]

function onAnimateImage(index: number, isHover: boolean = true) {
    if (!isDesktop.value) return

    const imageClass = `animate-image-${index}`

    if (!section.value) return

    const imageElement = section.value.querySelector(
        `#form-step-${index}`
    ) as HTMLImageElement

    if (isHover) {
        imageElement.classList.add(imageClass)
    } else {
        imageElement.classList.remove(imageClass)
    }
}

function onAnimateSpan(index: number, isHover: boolean = true) {
    if (!isDesktop.value) return

    const spanClass = "stepper-hover"

    if (!section.value) return

    const spanElement = section.value.querySelector(
        `#stepper-${index}`
    ) as HTMLImageElement

    if (isHover) {
        spanElement.classList.add(spanClass)
    } else {
        spanElement.classList.remove(spanClass)
    }
}
</script>

<template>
    <div class="how-participate" ref="section">
        <!-- Left Part -->
        <div class="how-participate__left">
            <h3 class="how-participate__title">
                Comment participer à
                <img
                    src="https://res.cloudinary.com/dcoutwkb1/image/upload/q_auto/v1745851008/logo_black_qzgz7i.svg"
                    alt="WattWatchers"
                    class="how-participate__title-logo"
                    loading="lazy"
                />
                ?
            </h3>
            <div class="how-participate__step-wrapper">
                <h4
                    :class="[
                        'how-participate__step-title',
                        !isMobile && 'big-underline big-underline--purple',
                    ]"
                >
                    10 questions en 3 étapes !
                </h4>
                <ul class="how-participate__step-list">
                    <li
                        v-animateonscrollv2="
                            isDesktop && {
                                enterClass:
                                    'animate-enter fade-in-10 animate-duration-500 slide-in-from-r-8',
                            }
                        "
                        @mouseenter="onAnimateImage(1)"
                        @mouseleave="onAnimateImage(1, false)"
                        :id="`stepper-${1}`"
                    >
                        <span class="stepper">1</span> Adresse et compteur
                        électrique
                    </li>
                    <li
                        v-animateonscrollv2="
                            isDesktop && {
                                enterClass:
                                    'animate-enter fade-in-10 animate-duration-700 slide-in-from-r-8',
                            }
                        "
                        @mouseenter="onAnimateImage(2)"
                        @mouseleave="onAnimateImage(2, false)"
                        :id="`stepper-${2}`"
                    >
                        <span class="stepper">2</span> L'essentiel
                    </li>
                    <li
                        v-animateonscrollv2="
                            isDesktop && {
                                enterClass:
                                    'animate-enter fade-in-10 animate-duration-1000 slide-in-from-r-8',
                            }
                        "
                        @mouseenter="onAnimateImage(3)"
                        @mouseleave="onAnimateImage(3, false)"
                        :id="`stepper-${3}`"
                    >
                        <span class="stepper">3</span> Facultatif, mais utile
                    </li>
                </ul>
            </div>

            <div v-if="isDesktop" class="how-participate__text-wrapper">
                <p class="how-participate__text">
                    <strong
                        >Vos données sont en sécurité et on ne vous recontactera
                        pas.</strong
                    >
                    <img
                        src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1745852810/icon_smile_g3q7sb.png"
                        alt="Smiley icon"
                        loading="lazy"
                        class="how-participate__icon-smiley"
                    />
                </p>
            </div>
        </div>
        <!-- Right Part -->
        <div class="how-participate__right">
            <img
                :src="illustration.logo"
                :alt="illustration.alt"
                v-for="(illustration, index) in formIllustrations"
                loading="lazy"
                :key="index"
                :id="`form-step-${index + 1}`"
                @mouseenter="onAnimateSpan(index + 1)"
                @mouseleave="onAnimateSpan(index + 1, false)"
            />
        </div>
        <div v-if="isTablet" class="how-participate__text-wrapper">
            <p class="how-participate__text">
                <strong
                    >Vos données sont en sécurité et on ne vous recontactera
                    pas.</strong
                >
                <img
                    src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1745852810/icon_smile_g3q7sb.png"
                    alt="Smiley icon"
                    loading="lazy"
                    class="how-participate__icon-smiley"
                />
            </p>
        </div>
    </div>
</template>

<style scoped>
@import "@styles/breakpoints.css";

@keyframes bounce-form1 {
    0% {
        transform: translate(-50%, -5%);
    }
    50% {
        transform: translate(-50%, 0%);
    }
    100% {
        transform: translate(-50%, -5%);
    }
}

@keyframes bounce-form2 {
    0% {
        transform: translate(-55%, -40%) rotate(-5deg);
    }
    50% {
        transform: translate(-60%, -40%) rotate(-7deg);
    }
    100% {
        transform: translate(-55%, -40%) rotate(-5deg);
    }
}

@keyframes bounce-form3 {
    0% {
        transform: translate(-35%, -40%) rotate(5deg);
    }
    50% {
        transform: translate(-35%, -40%) rotate(7deg);
    }
    100% {
        transform: translate(-35%, -40%) rotate(5deg);
    }
}

.how-participate {
    display: flex;

    justify-content: center;
    align-items: flex-start;
    padding: 8.75rem 0rem 5rem 0rem;
    gap: 1rem;
    align-self: stretch;

    @media (--large-viewport) {
        /** Pour s'assurer de bien commencer au début du block lors du scrollIntoView */
        scroll-margin-top: 5rem;
    }

    @media (--medium-viewport) {
        padding: 0;
        scroll-margin-top: 0;
    }

    /* Sur tablet */
    @media (--large-viewport) {
        flex-direction: column;
        gap: 4rem;
        align-items: center;
    }

    .how-participate__title {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        gap: 0.25rem;

        color: #092754;

        font-size: 2rem;
        font-weight: 600;

        .how-participate__title-logo {
            width: 15rem;
            height: 1.5rem;
        }

        &:before {
            animation-timeline: --how-participate;
        }
    }

    .how-participate__left {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        flex: 1 0 0;
        align-self: stretch;

        /* Sur tablet */
        @media (--large-viewport) {
            gap: 4rem;
        }
    }

    .how-participate__step-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2rem;

        .how-participate__step-title {
            color: #092754;
            text-align: center;
            font-size: 2rem;
            /* Sur tablet */
            @media (--medium-viewport) {
                padding: 0 0.5rem;
            }
        }
        .how-participate__step-list {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1.5rem;
            li {
                display: flex;
                align-items: center;
                gap: 0.5rem;

                color: #092754;
                font-family: Montserrat;
                font-size: 1.25rem;
                font-weight: 700;
                line-height: 100%; /* 1.25rem */
                cursor: default;

                transform: color 0.3s ease-in;

                &:hover {
                    color: #1d6ee7;
                }
            }
        }
    }

    .how-participate__text-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        align-self: stretch;
    }

    .how-participate__text {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #092754;
        text-align: center;

        strong {
            font-weight: 700;
        }
    }

    .how-participate__icon-smiley {
        width: 2.5rem;
        height: 2.5rem;
        aspect-ratio: 1/1;
    }

    .how-participate__right {
        position: relative;
        max-width: 30rem;
        width: 100%;
        height: 34rem;
        img {
            position: absolute;
            max-width: 20.7455rem;
            width: 100%;
            height: auto;
            transition: all 0.5s ease-in-out;

            @media (--small-viewport) {
                max-width: 15rem;
                width: 100%;
                height: auto;
            }
        }

        @media (--small-viewport) {
            height: 24rem;
        }

        img:nth-child(1) {
            bottom: 0;
            left: 50%;
            z-index: 3;
            transform: translate(-50%, 0%);
        }

        img:nth-child(2) {
            bottom: 0;
            left: 50%;
            z-index: 2;
            transform: translate(-60%, -30%) rotate(-6deg);
            transform-origin: bottom left;
        }

        img:nth-child(3) {
            bottom: 0;
            left: 50%;
            z-index: 1;
            transform: translate(-45%, -25%) rotate(6deg);
            transform-origin: bottom right;
        }

        @media not all and (--large-viewport) {
            img:nth-child(1) {
                &:hover {
                    transform: translate(-50%, 0) scale(1.05);
                }
            }
            img:nth-child(2) {
                &:hover {
                    transform: translate(-60%, -45%) rotate(-8deg) scale(1.05);
                }
            }
            img:nth-child(3) {
                &:hover {
                    transform: translate(-40%, -45%) rotate(8deg) scale(1.05);
                }
            }
        }

        transition: transform 0.5s ease-in-out;
    }
}

.animate-image-1 {
    transform: translate(-50%, 0%) scale(1.05) !important;
}
.animate-image-2 {
    transform: translate(-60%, -45%) rotate(-8deg) scale(1.05) !important;
}
.animate-image-3 {
    transform: translate(-40%, -45%) rotate(8deg) scale(1.05) !important;
}
.stepper-hover {
    transition: color 0.3s ease-in;
    color: #1d6ee7 !important;
}
.stepper {
    display: flex;
    width: 2rem;
    padding: 0.5625rem 0rem;
    justify-content: center;
    align-items: center;
    aspect-ratio: 1/1;
    border-radius: 6.1875rem;
    border: 1px solid var(--root-surface-border, #dfe7ef);
    background: #fff;

    color: #092754;

    /* panel/header/typography */
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 700;
    line-height: 100%; /* 0.875rem */
}
</style>
